<template>
  <div
    :class="{
      'has-avatar': !isHideAvatar || isKeepAvatarDesign,
      'no-padding': !hasMediaContent,
    }"
    class="content-container"
  >
    <default-overlay
      v-if="hasMediaContent || !allButtonsHidden"
    />
    <qr-code-component
      :qr-code-data="currentContent?.attrs?.qrCode"
      :has-margin="!!(headline.value || text.value || (buttons.length > 0 && !currentContent.attrs?.invisibleButtons))"
    />
    <media-text
      v-if="headline.value?.trim().length"
      :data="headline"
      :class="{
        headline: true,
        'text-margin': !text.value
      }"
      class="media-text"
      weight="bold"
    />
    <media-text
      v-if="text.value?.trim().length"
      :data="text"
      class="text media-text"
    />
    <div
      v-if="!currentContent.attrs?.invisibleButtons"
      :class="{
        'has-avatar': !isHideAvatar || isKeepAvatarDesign,
      }"
      class="buttons-container"
    >
      <div
        v-for="({ button, next }, idx) in buttons"
        :key="idx"
        :style="idx === selectedButton ? buttonActiveStyle : buttonInactiveStyles"
        :class="{
          'has-avatar': !isHideAvatar || isKeepAvatarDesign,
          'hidden': button.isHidden,
          'no-match': !button.isMatch
        }"
        class="button"
        @click="interruptAndRun(idx, next)"
      >
        <div
          :dir="languageDir"
          class="button-text"
        >
          {{ button.text }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStt, removeEmojisFromString } from '@ht/lib-plural-vue';
import useSequence from '@/composables/useSequence';
import useContent from '@/composables/useContent';
import useConfig from '@/composables/useConfig';
import useConditions from '@/composables/useConditions';
import useChatbot from '@/composables/useChatbot';
import useAnalytics from '@/composables/useAnalytics';
import useDynamicText from '@/composables/useDynamicText';
import useDynamicButtons from '@/composables/useDynamicButtons';
import QrCodeComponent from '@/components/QrCodeComponent';
import DefaultOverlay from '@/components/DefaultOverlay';
import MediaText from '@/components/MediaText';
import { INTERACTION_DELAY, SYSTEM_VARIABLES_MAP, VARIABLE_VALUE_TYPES } from '@/js/constants';
import FontLoader from '@/js/fontLoader';
import { getDynamicObjects } from '@/js/dynamicData';

export default defineComponent({
  name: 'QuestionComponent',

  components: {
    MediaText,
    DefaultOverlay,
    QrCodeComponent,
  },

  setup() {
    const { runSequence, interruptSequence, currentSeq } = useSequence();
    const {
      currentContent,
      isHideAvatar,
      hasMediaContent,
      isKeepAvatarDesign,
    } = useContent();
    const { recognizedText, sttStart, sttStop, sttPhrases } = useStt();
    const { amendAction, lastAnalyticData } = useAnalytics();
    const { setVariables, languageDir, getVariable, setFlowStateBackToStart } = useConfig();
    const { checkCondition } = useConditions();

    const headlineText = useDynamicText(currentContent.value?.attrs?.headline);
    const textText = useDynamicText(currentContent.value?.attrs?.text);

    const headline = computed(() => ({
      ...headlineText.value,
      fontSize: headlineText.value.fontSize ?? 56,
    }));

    const text = computed(() => ({
      ...textText.value,
      fontSize: textText.value.fontSize ?? 36,
    }));

    const formattedButtons = computed(() => {
      if (currentContent.value.attrs.useDynamicButtons) {
        const { value: dynamicButtonsVariableValue } = getVariable(currentContent.value.attrs.dynamicButtonsVariableId)
          || {};

        try {
          const payload = typeof dynamicButtonsVariableValue === 'string' ? JSON.parse(dynamicButtonsVariableValue) : dynamicButtonsVariableValue;
          const dynamicButtons = getDynamicObjects(payload);
          return dynamicButtons.map(({ label, value }) => ({
            button: {
              text: label,
              isMatch: true,
              variations: [label],
              variables: [
                { id: currentContent.value.attrs.variableId, value: { type: VARIABLE_VALUE_TYPES.PRIMITIVE, value } },
              ],
            },
            next: currentContent.value.attrs.next,
          }));
        } catch (err) {
          return [];
        }
      }

      return currentContent.value?.attrs?.answers.map(({ next, ...button }) => ({
        button: {
          ...button,
          isMatch: button.condition ? checkCondition(button.condition) : true,
        },
        next,
      }));
    });
    const buttons = useDynamicButtons(formattedButtons.value);
    const allButtonsHidden = computed(
      () => currentContent.value.attrs?.invisibleButtons || buttons.value.every((i) => i.button.isHidden),
    );

    const selectedButton = ref(-1);
    const buttonInactiveStyles = computed(() => ({
      backgroundColor: currentContent.value.styles?.button_inactive_background_color,
      color: currentContent.value.styles?.button_inactive_text_color,
      fontFamily: FontLoader.buildFontFamily(currentContent.value.styles?.answers_font),
      '--font-size': `${currentContent.value.styles?.answers_fontSize ?? 28}px`,
    }));
    const buttonActiveStyle = computed(() => ({
      backgroundColor: currentContent.value.styles?.button_active_background_color,
      color: currentContent.value.styles?.button_active_text_color,
      fontFamily: FontLoader.buildFontFamily(currentContent.value.styles?.answers_font),
      '--font-size': `${currentContent.value.styles?.answers_fontSize ?? 28}px`,
    }));

    async function interruptAndRun(index, id) {
      if (selectedButton.value !== -1) {
        return;
      }

      selectedButton.value = index;

      amendAction({
        questionButtonSelected: buttons.value[index].button.text,
      });

      if (buttons.value[index].button.variables) {
        setVariables(buttons.value[index].button.variables);
      }

      setTimeout(() => {
        if (id) {
          interruptSequence();
          setFlowStateBackToStart(id);
          runSequence(id);
        } else {
          selectedButton.value = -1;
        }
      }, INTERACTION_DELAY); // nextTick will not wait for rerender of selected button
    }

    const { askChatbot } = useChatbot();

    watch(recognizedText, async (value) => {
      const recText = value.text.trim();
      if (recText && selectedButton.value === -1 && value.isFinal) {
        const index = buttons.value.findIndex(
          ({ button }) => button.variations.some(
            (v) => v?.length && recText.toLowerCase().includes(
              removeEmojisFromString(v).toLowerCase().replace('\n', ' '),
            ) && !button.isHidden && button.isMatch,
          ),
        );
        if (index !== -1) {
          const button = buttons.value[index];
          if (button.next) {
            interruptAndRun(index, button.next);
          }
        } else if (currentContent.value.attrs.usePluralDialogs) {
          await askChatbot(recText);
        } else if (currentContent.value.attrs.anythingElse) {
          const next = currentContent.value.attrs.anythingElse;
          interruptSequence();
          setFlowStateBackToStart(next);
          runSequence(next);
        }
      }
    });

    onMounted(() => {
      setVariables([{
        id: SYSTEM_VARIABLES_MAP.CURRENT_DESIGN_ELEMENT.id,
        value: { value: currentSeq.value.id },
      }]);
      sttPhrases(buttons.value
        .map(({ button }) => ({ variations: button.variations.filter((v) => v?.length) }))
        .filter(({ variations }) => variations.length));
      sttStart({
        sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
        elementId: lastAnalyticData.value.elementId,
      });
    });
    onBeforeUnmount(() => {
      sttStop();
    });

    return {
      selectedButton,
      currentContent,
      headline,
      text,
      buttons,
      isHideAvatar,
      isKeepAvatarDesign,
      buttonInactiveStyles,
      buttonActiveStyle,
      hasMediaContent,
      interruptAndRun,
      allButtonsHidden,
      languageDir,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "sass:math";
@import "@/scss/common-ui.scss";

.content-container {
  @extend .content-container;
}

.buttons-container {
  @extend .buttons-container;
}

.button {
  @extend .button;
}

.button-text {
  @extend .button-text;
}
</style>
